import { CreditCard } from '@accedo/one-plugin-sdk';
import styled, { css } from 'styled-components';

import { FormInputTypes, TFormData } from '#/client/models';
import { getFieldWidth } from '#/components/Form/Form.styled';
import { InputField } from '#/components/Form/Form.types';
import { MEDIA_QUERIES } from '#/hooks/useResponsiveValue';
import {
  BreakpointKeys,
  CONTENT_MAX_WIDTH_PX,
  FormInputVariants,
} from '#/service/constants';
import { remify } from '#/service/utils';
import { tb } from '#/store/translation/translation.store';

import { CvvHelp } from './CvvHelp';
import { PaymentFormKey } from './models';

interface PlanPickerSubTextProps {
  $isCentered?: boolean;
  $isTextCentered?: boolean;
}

export const cssPlanContainerBase = css`
  border-radius: ${remify(8)};
  padding: ${remify(24)};
  background-color: var(--pageSecondaryBackground);
  color: var(--pageParagraphForeground);
`;

export const PlanPickerGrid = styled.div`
  display: grid;
  grid-template-columns: auto max-content;
  column-gap: ${remify(32)};
  align-items: center;
  max-width: 100%;
  min-width: 0;

  @media ${MEDIA_QUERIES[BreakpointKeys.XS]} {
    grid-template-columns: 1fr;
  }
`;

PlanPickerGrid.displayName = 'PlanPickerGrid';

export const PlanPickerMainText = styled.div.attrs(() => ({
  'data-testid': 'plan-picker-main-text',
}))`
  font: var(--fontStyleSubtitle);
`;

PlanPickerMainText.displayName = 'PlanPickerMainText';

export const PlanPickerSubText = styled.div.attrs(() => ({
  'data-testid': 'plan-picker-sub-text',
}))<PlanPickerSubTextProps>`
  font: var(--fontStyleBody);
  margin-bottom: 1rem;
  max-width: ${remify(CONTENT_MAX_WIDTH_PX)};
  min-width: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  hyphens: auto;
  ${({ $isCentered }) => $isCentered && `margin-inline: auto;`}
  ${({ $isTextCentered }) => $isTextCentered && `text-align: center;`}
`;

PlanPickerSubText.displayName = 'PlanPickerSubText';

export const getPaymentInputFields = (
  isPaymentDetailsRequired: boolean
): InputField[] => {
  return [
    {
      key: PaymentFormKey.CardHolderName,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.TEXT,
      label: tb('iap.inputField.cardHolderName.label'),
      required: isPaymentDetailsRequired,
    },
    {
      key: PaymentFormKey.CardNumber,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.NUMBER,
      label: tb('iap.inputField.cardNumber.label'),
      required: isPaymentDetailsRequired,
      format: '#### #### #### ####',
    },
    {
      key: PaymentFormKey.ExpirationDate,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.NUMBER,
      label: tb('iap.inputField.expirationDate.label'),
      required: isPaymentDetailsRequired,
      format: '##/##',
      width: getFieldWidth(2),
    },
    {
      key: PaymentFormKey.SecurityCode,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.TEXT,
      label: tb('iap.inputField.securityCode.label'),
      required: isPaymentDetailsRequired,
      help: <CvvHelp>{tb('iap.inputField.securityCode.help')}</CvvHelp>,
      width: getFieldWidth(2),
    },
    {
      key: PaymentFormKey.BillingZipCode,
      type: FormInputTypes.INPUT,
      variant: FormInputVariants.TEXT,
      label: tb('iap.inputField.billingZipCode.label'),
      required: isPaymentDetailsRequired,
    },
  ];
};

export const getVoucherInputField = (): InputField => {
  return {
    key: 'voucher',
    type: FormInputTypes.INPUT,
    variant: FormInputVariants.TEXT,
    label: tb('iap.inputField.voucher.label'),
    required: false,
  };
};

export const formDataToCreditCard = (
  formData: TFormData
): CreditCard | undefined => {
  const cardHolderName = formData[PaymentFormKey.CardHolderName] as string;
  const cardNumber = (formData[PaymentFormKey.CardNumber] as string) ?? '';
  const expiryDate = formData[PaymentFormKey.ExpirationDate] as string;
  const cvv = formData[PaymentFormKey.SecurityCode] as string;
  const billingZip = formData[PaymentFormKey.BillingZipCode] as string;

  // if all fields are empty, that means we're trying to subscribe without
  // providing credit card details which can be allowed by some vouchers.
  const isEmpty = ![
    cardHolderName,
    cardNumber,
    expiryDate,
    cvv,
    billingZip,
  ].some(Boolean);

  if (isEmpty) {
    return undefined;
  }

  const expirationSegments = expiryDate?.split('/') || [0, 0];
  const expirationMonth = +expirationSegments[0];
  const expirationYear = +expirationSegments[1];

  return {
    cardHolderName,
    cardNumber: cardNumber.replace(/\s/g, ''),
    expiryDate,
    expirationMonth,
    expirationYear,
    cvv,
    billingZip,
  };
};
